import {
  InventoryStructuredTableSchemaType,
  InventoryTableMetadata,
} from 'notebook/integrations/samples/InventoryTableConstants';
import {
  ALL_PARAMETER_STRUCTURED_TABLE_KINDS,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {isSchemaWithDescription} from 'schemas/SchemaConstants';

/**
 * The title bar tooltip describing the table's schema, or null when there is nothing to show.
 *
 * A single schema shows its name (and description, when present); several schemas collapse to
 * "Multiple schemas". Parameter tables are excluded because they usually carry several parameter
 * schemas. Inventory tables that accept any plate or container fall back to an "Any ..." label.
 */
export function computeTitleTooltip(
  metadata: Table['metadata'],
  tableKind: StructuredTableKind | null
): string | null {
  let titleTooltip: string | null = null;

  if (
    metadata &&
    'schemas' in metadata &&
    metadata.schemas.length > 0 &&
    (tableKind == null || !ALL_PARAMETER_STRUCTURED_TABLE_KINDS.includes(tableKind))
  ) {
    if (metadata.schemas.length > 1) {
      titleTooltip = 'Multiple schemas';
    } else {
      const schemaName = metadata.schemas[0]?.name;
      const schemaDescription = isSchemaWithDescription(metadata.schemas[0])
        ? metadata.schemas[0].schemaDescription
        : undefined;
      if (schemaName) {
        titleTooltip = `Schema: ${schemaName}`;
        if (schemaDescription) {
          titleTooltip = `${titleTooltip}\n\nDescription: ${schemaDescription}`;
        }
      }
    }
  }

  if (!titleTooltip && tableKind === StructuredTableKind.INVENTORY) {
    const storableSchemaType = (metadata as StructuredTableToTableMetadata<InventoryTableMetadata>)?.metadata
      .storableSchemaType;

    if (storableSchemaType === InventoryStructuredTableSchemaType.PLATE) {
      titleTooltip = 'Any plate (no schema)';
    } else if (storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER) {
      titleTooltip = 'Any container (no schema)';
    }
  }

  return titleTooltip;
}

function computeStatusText(
  isConfigTable: boolean,
  tableStatus: TableIndicatorStatus | null | undefined
): string {
  // Config tables never describe a status.
  if (isConfigTable) {
    return '';
  }
  switch (tableStatus) {
    case TableIndicatorStatus.ERROR:
      return 'Some values with error';
    case TableIndicatorStatus.IMMUTABLE:
      return 'This table is a snapshot of the data at the time of its creation, which has subsequently been archived or modified';
    case TableIndicatorStatus.OUT_OF_DATE:
      return 'Some values out of date';
    case TableIndicatorStatus.ROWS_ARCHIVED:
      return 'Some values archived';
    case TableIndicatorStatus.ALL_ROWS_ARCHIVED_UNSUBMITTED:
      return 'All values archived';
    case TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED:
      return 'Table or required schemas archived';
    case TableIndicatorStatus.UNSUBMITTED_CHANGES:
      return 'Some values not submitted';
    default:
      return '';
  }
}

/**
 * The tooltip shown on the table name: the table status description, prefixed by the legacy
 * `nameTooltip` when one is set.
 */
export function computeStatusTooltip(
  isConfigTable: boolean,
  tableStatus: TableIndicatorStatus | null | undefined,
  nameTooltip: string | null | undefined
): string {
  const statusText = computeStatusText(isConfigTable, tableStatus);
  if (!nameTooltip) {
    return statusText;
  }
  return statusText ? `${nameTooltip}\n${statusText}` : nameTooltip;
}
